import React, { useEffect, useState, useRef } from 'react';
import styled, { css, keyframes } from 'styled-components';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import currency from 'helpers/currency';
import date from 'helpers/date';
import QRCode from 'qrcode.react';
import Modal from 'components/_UI/Modal';
import DynamicProfileBackground from 'components/commons/DynamicProfileBackground';
import EstablishmentOrderStatusChips from 'modules/dashboard/orders/EstablishmentOrders/EstablishmentOrderStatusChips';
import pusherService from 'services/pusher';
import { useBackButtonCollapse } from 'context/general/BackButtonCollapse';
import { useInvisibleButton } from 'context/general/InvisibleButton';
import { useCollapse } from 'context/general/Collapse';
import { ArrowLeft, Calendar, MapPin, DollarSign, ChevronLeft, ChevronRight, Send, Download, Loader, Award } from 'lucide-react';
import ticketsApi from 'api/requests/tickets';
import certificateApi from 'api/requests/certificate';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';
import WalletSessions from './WalletSessions';

function sortUsedLast(items) {
    return [...items].sort((a, b) => {
        const aUsed = a.used_at ? 1 : 0;
        const bUsed = b.used_at ? 1 : 0;
        return aUsed - bUsed;
    });
}

export default function WalletDetail({ data, onBack }) {
    const [tickets, setTickets] = useState(sortUsedLast(data?.tickets?.filter(ticket => ticket.type === 'ticket') || []));
    const [products, setProducts] = useState(sortUsedLast(data?.tickets?.filter(ticket => ticket.type === 'product') || []));

    const totalPrice = tickets.concat(products).reduce((sum, ticket) => sum + (Number(ticket.price) || 0), 0);
    const { backButtonCollapse, setBackButtonCollapse } = useBackButtonCollapse();
    const { setInvisibleButton } = useInvisibleButton();

    const [ticketStatus, setTicketStatus] = useState([]);
    const [productStatus, setProductStatus] = useState([]);
    const [visibleQrCode, setVisibleQrCode] = useState(false);
    const { collapse } = useCollapse();

    const [activeTicketIdx, setActiveTicketIdx] = useState(0);
    const [activeProductIdx, setActiveProductIdx] = useState(0);
    const ticketScrollRef = useRef(null);
    const productScrollRef = useRef(null);

    const [transferModalOpen, setTransferModalOpen] = useState({});
    const [transferForm, setTransferForm] = useState({ email: '', name: '' });
    const [transferLoading, setTransferLoading] = useState(false);
    const [transferError, setTransferError] = useState('');
    const [downloadLoading, setDownloadLoading] = useState({});
    const [certificateDownloadLoading, setCertificateDownloadLoading] = useState(false);
    const { toast, toasts } = useToast();

    const isEventFinished = data?.dates?.length > 0 && data.dates.every(d => {
        return new Date(`${d.date}T${d.end}`) < new Date()
    });

    useEffect(() => {
        setBackButtonCollapse({
            ...backButtonCollapse,
            withHideCity: false,
            visible: true
        });

        if (data?.dates) {
            const currentDate = new Date();
            const statusTickets = tickets.map(ticket => {
                const isEventFinished = data?.dates.every(d => {
                    const eventEndDate = new Date(`${d.date}T${d.end}`);
                    return eventEndDate < currentDate;
                });
                if (isEventFinished && !ticket?.used_at) return 'expired';
                if (ticket.used_at) return 'used';
                return 'active';
            });
            setTicketStatus(statusTickets);

            const statusProducts = products?.map(ticket => {
                const eventEndDate = new Date(`${data?.dates[0]?.date}T${data?.dates[0]?.end}`);
                if (eventEndDate < currentDate && !ticket?.used_at) return 'expired';
                if (ticket.used_at) return 'used';
                return 'active';
            });
            setProductStatus(statusProducts);
        }

        if (!data?.dates) {
            const statusProducts = products?.map(ticket => {
                if (ticket.used_at) return 'used';
                return 'active';
            });
            setProductStatus(statusProducts);
        }

        if (data.has_sectors) {
            const handleEvent = (updatedOrder) => {
                if (!updatedOrder || !updatedOrder.order_id) return;
                setProducts((prev) =>
                    prev.map(product => {
                        if (product?.order?.id === updatedOrder.order_id) {
                            return {
                                ...product,
                                order: {
                                    ...product.order,
                                    fulfillment_status: {
                                        status: updatedOrder.status,
                                        updated_at: updatedOrder.updated_at
                                    }
                                }
                            };
                        }
                        return product;
                    })
                );
            };

            pusherService.subscribe(`establishments.${data.id}`, 'order.fulfillment_status.updated', handleEvent);
            return () => {
                pusherService.unsubscribe(`establishments.${data.id}`, 'order.fulfillment_status.updated', () => false);
            };
        }
    }, []);

    function openModalQRCode(id) {
        setVisibleQrCode((prev) => ({ ...prev, [id]: true }));
        setInvisibleButton(true);
    }

    function closeModalQRCode(id) {
        setVisibleQrCode((prev) => ({ ...prev, [id]: false }));
        setInvisibleButton(false);
    }

    function handleScroll(ref, setIdx) {
        if (!ref.current) return;
        const scrollLeft = ref.current.scrollLeft;
        const cardWidth = ref.current.firstChild?.offsetWidth || 1;
        const gap = 16;
        setIdx(Math.round(scrollLeft / (cardWidth + gap)));
    }

    function scrollTo(ref, idx) {
        if (!ref.current) return;
        const cardWidth = ref.current.firstChild?.offsetWidth || 1;
        const gap = 16;
        ref.current.scrollTo({ left: idx * (cardWidth + gap), behavior: 'smooth' });
    }

    function openTransferModal(id) {
        setTransferForm({ email: '', name: '' });
        setTransferError('');
        setTransferModalOpen((prev) => ({ ...prev, [id]: true }));
    }

    function closeTransferModal(id) {
        setTransferModalOpen((prev) => ({ ...prev, [id]: false }));
        setTransferError('');
    }

    async function handleTransfer(ticketId) {
        if (!transferForm.name.trim() || !transferForm.email.trim()) {
            setTransferError('Preencha todos os campos.');
            return;
        }

        setTransferLoading(true);
        setTransferError('');

        try {
            await ticketsApi.transfer(ticketId, {
                email: transferForm.email.trim(),
                name: transferForm.name.trim(),
            });

            closeTransferModal(ticketId);
            toast.success('Ingresso transferido com sucesso!');

            setTickets((prev) => {
                const updated = prev.filter((t) => t.id !== ticketId);
                if (activeTicketIdx >= updated.length && updated.length > 0) {
                    setActiveTicketIdx(updated.length - 1);
                }
                return updated;
            });
        } catch (err) {
            const status = err?.response?.status;
            const message = err?.response?.data?.message;

            if (status === 422 && message) {
                setTransferError(message);
            } else if (message) {
                setTransferError(message);
            } else {
                toast.error('Ocorreu um erro inesperado. Tente novamente.');
                closeTransferModal(ticketId);
            }
        } finally {
            setTransferLoading(false);
        }
    }

    async function handleCertificateDownload() {
        if (certificateDownloadLoading) return;
        setCertificateDownloadLoading(true);
        try {
            const response = await certificateApi.download(data.certificate.slug);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'certificado.pdf';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Erro ao baixar o certificado. Tente novamente.');
        } finally {
            setCertificateDownloadLoading(false);
        }
    }

    async function handleDownload(ticketId) {
        if (downloadLoading[ticketId]) return;
        setDownloadLoading((prev) => ({ ...prev, [ticketId]: true }));
        try {
            const response = await ticketsApi.download(ticketId);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'ingresso.pdf';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Erro ao baixar o ingresso. Tente novamente.');
        } finally {
            setDownloadLoading((prev) => ({ ...prev, [ticketId]: false }));
        }
    }

    const eventImage = data?.path || (data?.images?.length && data.images[0]) || null;

    const isEventWithin24Hours = (() => {
        if (!data?.dates?.[0]) return false;
        const eventStart = new Date(`${data.dates[0].date}T${data.dates[0].start || '00:00:00'}`);
        return (eventStart - new Date()) <= 24 * 60 * 60 * 1000;
    })();

    return (
        <Container>
            <ToastContainer toasts={toasts} />
            {collapse?.height !== 'max' && (
                <TopBar>
                    <BackButton onClick={onBack}>
                        <ArrowLeft size={20} color={colors.nightDark} />
                    </BackButton>
                    <TopBarTitle>Detalhes do pedido</TopBarTitle>
                    <TopBarSpacer />
                </TopBar>
            )}

            <Content>
                {/* Event Hero */}
                <EventCard>
                    <EventImageWrapper>
                        {eventImage ? (
                            <EventImage src={eventImage} alt={data?.name} />
                        ) : (
                            <DynamicProfileBackground
                                img={data?.images}
                                word={data?.name}
                                size={'100%'}
                                event
                            />
                        )}
                    </EventImageWrapper>

                    <EventBody>
                        <EventName>{data?.name}</EventName>

                        <EventInfoList>
                            {data?.dates && (
                                <EventInfoItem>
                                    <Calendar size={15} color={colors.occasionalPurple} />
                                    <EventInfoText>
                                        {date.format(data.dates[0].date)} &bull; {date.getTimeWithoutSeconds(data?.dates[0].start)}
                                        {data?.dates[0].end ? ` - ${date.getTimeWithoutSeconds(data?.dates[0].end)}` : ''}
                                    </EventInfoText>
                                </EventInfoItem>
                            )}

                            {data?.address && (
                                <EventInfoItem>
                                    <MapPin size={15} color={colors.occasionalPurple} />
                                    <EventInfoText>
                                        {data.address?.raw_address
                                            ? data.address.raw_address
                                            : <>
                                                {data.address?.street?.name && `${data.address.street.name},`} {data.address?.street?.district && `Bairro ${data.address.street.district}`}
                                                {data.address?.number && `, nº ${data.address.number}`}
                                                {data.address?.complement && ` - ${data.address.complement}`}
                                            </>
                                        }
                                    </EventInfoText>
                                </EventInfoItem>
                            )}

                            <EventInfoItem>
                                <DollarSign size={15} color={colors.occasionalPurple} />
                                <EventInfoText>Total: {currency.format(totalPrice)}</EventInfoText>
                            </EventInfoItem>
                        </EventInfoList>
                        {data?.certificate && isEventFinished && (
                            <CertificateSection>
                                <CertificateSectionHeader>
                                    <Award size={15} color={colors.occasionalPurple} />
                                    <CertificateSectionTitle>Certificado de participação</CertificateSectionTitle>
                                </CertificateSectionHeader>
                                <CertificateDownloadButton
                                    disabled={certificateDownloadLoading}
                                    onClick={handleCertificateDownload}
                                >
                                    {certificateDownloadLoading ? (
                                        <>
                                            <SpinnerIcon><Loader size={14} /></SpinnerIcon>
                                            Baixando...
                                        </>
                                    ) : (
                                        <>
                                            <Download size={14} />
                                            Baixar certificado
                                        </>
                                    )}
                                </CertificateDownloadButton>
                            </CertificateSection>
                        )}
                    </EventBody>
                </EventCard>

                {/* Tickets Carousel */}
                {tickets?.length > 0 && (
                    <Section>
                        <SectionHeader>
                            <SectionTitle>Ingressos</SectionTitle>
                            <SectionHint>Apresente o QR Code no check-in</SectionHint>
                        </SectionHeader>

                        {tickets.length > 1 && (
                            <CarouselNav>
                                <NavArrow
                                    disabled={activeTicketIdx === 0}
                                    onClick={() => { scrollTo(ticketScrollRef, activeTicketIdx - 1); }}
                                >
                                    <ChevronLeft size={18} />
                                </NavArrow>
                                <CarouselCounter>{activeTicketIdx + 1} de {tickets.length}</CarouselCounter>
                                <NavArrow
                                    disabled={activeTicketIdx === tickets.length - 1}
                                    onClick={() => { scrollTo(ticketScrollRef, activeTicketIdx + 1); }}
                                >
                                    <ChevronRight size={18} />
                                </NavArrow>
                            </CarouselNav>
                        )}

                        <Carousel
                            ref={ticketScrollRef}
                            onScroll={() => handleScroll(ticketScrollRef, setActiveTicketIdx)}
                        >
                            {tickets.map((el, index) => (
                                <TicketCard key={el.id} status={ticketStatus[index]}>
                                    {(ticketStatus[index] === 'expired' || ticketStatus[index] === 'used') && (
                                        <StatusBanner status={ticketStatus[index]}>
                                            {ticketStatus[index] === 'expired' ? 'Expirado' : 'Utilizado'}
                                        </StatusBanner>
                                    )}

                                    <TicketTop dimmed={ticketStatus[index] === 'expired' || ticketStatus[index] === 'used'}>
                                        <TicketName>{el.name}</TicketName>

                                        <TicketDetailRow>
                                            <TicketLabel>Participante</TicketLabel>
                                            <TicketValue>{el?.participant}</TicketValue>
                                        </TicketDetailRow>

                                        {el.discounted_price !== null && el.discounted_price < el.price ? (
                                            <>
                                                <TicketDetailRow>
                                                    <TicketLabel>Valor original</TicketLabel>
                                                    <TicketValue style={{ textDecoration: 'line-through', color: colors.nightLight }}>{currency.format(el.price)}</TicketValue>
                                                </TicketDetailRow>
                                                <TicketDetailRow>
                                                    <TicketLabel>Valor pago</TicketLabel>
                                                    <TicketValue style={{ color: colors.success }}>{currency.format(el.discounted_price)}</TicketValue>
                                                </TicketDetailRow>
                                            </>
                                        ) : (
                                            <TicketDetailRow>
                                                <TicketLabel>Valor pago</TicketLabel>
                                                <TicketValue>{currency.format(el.price)}</TicketValue>
                                            </TicketDetailRow>
                                        )}
                                    </TicketTop>

                                    <TicketCutout />

                                    <TicketBottom dimmed={ticketStatus[index] === 'expired' || ticketStatus[index] === 'used'}>
                                        {ticketStatus[index] !== 'used' && ticketStatus[index] !== 'expired' ? (
                                            <TicketActionsWrapper>
                                                <TicketActions>
                                                    <QrButton onClick={(e) => { e.stopPropagation(); openModalQRCode(el.id); }}>
                                                        Ver QR Code
                                                    </QrButton>
                                                    {!isEventWithin24Hours && data?.allow_ticket_transfer !== false && (
                                                        <TransferButton onClick={(e) => { e.stopPropagation(); openTransferModal(el.id); }}>
                                                            <Send size={14} />
                                                            Transferir
                                                        </TransferButton>
                                                    )}
                                                </TicketActions>
                                                <DownloadButton
                                                    disabled={downloadLoading[el.id]}
                                                    onClick={(e) => { e.stopPropagation(); handleDownload(el.id); }}
                                                >
                                                    {downloadLoading[el.id] ? (
                                                        <>
                                                            <SpinnerIcon><Loader size={14} /></SpinnerIcon>
                                                            Baixando...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Download size={14} />
                                                            Download Ingresso
                                                        </>
                                                    )}
                                                </DownloadButton>
                                            </TicketActionsWrapper>
                                        ) : (
                                            <QrCodeWrapper>
                                                <QRCode
                                                    value={el.code}
                                                    size={100}
                                                    bgColor="#ffffff"
                                                    fgColor="#000000"
                                                    level="Q"
                                                    renderAs="svg"
                                                    includeMargin={false}
                                                />
                                            </QrCodeWrapper>
                                        )}
                                        <CodeText>Código: <CodeValue>{el?.code}</CodeValue></CodeText>
                                    </TicketBottom>

                                    {visibleQrCode[el.id] && (
                                        <Modal isOpen={visibleQrCode[el.id]} onClose={() => closeModalQRCode(el.id)}>
                                            <ModalContent>
                                                <ModalTitle>Apresente este QR Code no check-in</ModalTitle>
                                                <QrCodeWrapper>
                                                    <QRCode
                                                        value={el.code}
                                                        size={180}
                                                        bgColor="#ffffff"
                                                        fgColor="#000000"
                                                        level="Q"
                                                        renderAs="svg"
                                                        includeMargin={false}
                                                    />
                                                </QrCodeWrapper>
                                                <CodeText style={{ marginTop: 12 }}>Código: <CodeValue>{el?.code}</CodeValue></CodeText>
                                                <ModalCloseBtn onClick={() => closeModalQRCode(el.id)}>Fechar</ModalCloseBtn>
                                            </ModalContent>
                                        </Modal>
                                    )}

                                    {transferModalOpen[el.id] && (
                                        <Modal isOpen={transferModalOpen[el.id]} onClose={() => closeTransferModal(el.id)} closeButton>
                                            <TransferModalContent>
                                                <TransferModalTitle>Transferir ingresso</TransferModalTitle>
                                                <TransferModalSubtitle>Insira os dados de quem vai receber o ingresso</TransferModalSubtitle>

                                                <TransferFieldGroup>
                                                    <TransferInputLabel htmlFor={`transfer-name-${el.id}`}>Nome completo</TransferInputLabel>
                                                    <TransferInput
                                                        id={`transfer-name-${el.id}`}
                                                        type="text"
                                                        placeholder="Nome do destinatário"
                                                        value={transferForm.name}
                                                        onChange={(e) => setTransferForm((prev) => ({ ...prev, name: e.target.value }))}
                                                    />
                                                </TransferFieldGroup>

                                                <TransferFieldGroup>
                                                    <TransferInputLabel htmlFor={`transfer-email-${el.id}`}>E-mail</TransferInputLabel>
                                                    <TransferInput
                                                        id={`transfer-email-${el.id}`}
                                                        type="email"
                                                        placeholder="email@exemplo.com"
                                                        value={transferForm.email}
                                                        onChange={(e) => setTransferForm((prev) => ({ ...prev, email: e.target.value }))}
                                                    />
                                                </TransferFieldGroup>

                                                {transferError && <ErrorText>{transferError}</ErrorText>}

                                                <TransferSubmitButton onClick={() => handleTransfer(el.id)} disabled={transferLoading}>
                                                    {transferLoading ? 'Transferindo...' : 'Transferir'}
                                                </TransferSubmitButton>
                                                <ModalCloseBtn onClick={() => closeTransferModal(el.id)}>Cancelar</ModalCloseBtn>
                                            </TransferModalContent>
                                        </Modal>
                                    )}
                                </TicketCard>
                            ))}
                        </Carousel>

                        {tickets.length > 1 && (
                            <Dots>
                                {tickets.map((_, i) => (
                                    <Dot key={i} active={i === activeTicketIdx} onClick={() => scrollTo(ticketScrollRef, i)} />
                                ))}
                            </Dots>
                        )}
                    </Section>
                )}

                {/* Sessões da programação em que a pessoa marcou presença */}
                <WalletSessions sessions={data?.sessions} />

                {/* Products Carousel */}
                {products?.length > 0 && (
                    <Section>
                        <SectionHeader>
                            <SectionTitle>Produtos</SectionTitle>
                            <SectionHint>Apresente o QR Code ao retirar seu produto</SectionHint>
                        </SectionHeader>

                        {products.length > 1 && (
                            <CarouselNav>
                                <NavArrow
                                    disabled={activeProductIdx === 0}
                                    onClick={() => { scrollTo(productScrollRef, activeProductIdx - 1); }}
                                >
                                    <ChevronLeft size={18} />
                                </NavArrow>
                                <CarouselCounter>{activeProductIdx + 1} de {products.length}</CarouselCounter>
                                <NavArrow
                                    disabled={activeProductIdx === products.length - 1}
                                    onClick={() => { scrollTo(productScrollRef, activeProductIdx + 1); }}
                                >
                                    <ChevronRight size={18} />
                                </NavArrow>
                            </CarouselNav>
                        )}

                        <Carousel
                            ref={productScrollRef}
                            onScroll={() => handleScroll(productScrollRef, setActiveProductIdx)}
                        >
                            {products.map((el, index) => (
                                <TicketCard key={el.id} status={productStatus[index]}>
                                    {(productStatus[index] === 'expired' || productStatus[index] === 'used') && (
                                        <StatusBanner status={productStatus[index]}>
                                            {productStatus[index] === 'expired' ? 'Expirado' : 'Utilizado'}
                                        </StatusBanner>
                                    )}

                                    <TicketTop dimmed={productStatus[index] === 'expired' || productStatus[index] === 'used'}>
                                        <ProductHeader>
                                            {el?.image ? (
                                                <ProductImage src={el.image} alt={el.name} />
                                            ) : (
                                                <ProductImage src={'../../assets/images/placeholder-products.png'} alt={el.name} />
                                            )}
                                            <ProductInfo>
                                                <TicketName>{el.name}</TicketName>
                                                {el.discounted_price !== null ? (
                                                    <>
                                                        <TicketDetailRow>
                                                            <TicketLabel>Original</TicketLabel>
                                                            <TicketValue style={{ textDecoration: 'line-through', color: colors.nightLight }}>{currency.format(el.price)}</TicketValue>
                                                        </TicketDetailRow>
                                                        <TicketDetailRow>
                                                            <TicketLabel>Pago</TicketLabel>
                                                            <TicketValue style={{ color: colors.success }}>{currency.format(el.discounted_price)}</TicketValue>
                                                        </TicketDetailRow>
                                                    </>
                                                ) : (
                                                    <TicketDetailRow>
                                                        <TicketLabel>Valor pago</TicketLabel>
                                                        <TicketValue>{currency.format(el.price)}</TicketValue>
                                                    </TicketDetailRow>
                                                )}
                                            </ProductInfo>
                                        </ProductHeader>

                                        {el?.order?.id && (
                                            <TicketDetailRow>
                                                <TicketLabel>ID do pedido</TicketLabel>
                                                <TicketValue>{el.order.id}</TicketValue>
                                            </TicketDetailRow>
                                        )}
                                        {!!el?.order?.fulfillment_status?.updated_at && (
                                            <TicketDetailRow>
                                                <TicketLabel>Atualizado em</TicketLabel>
                                                <TicketValue>{date.format(new Date(el.order.fulfillment_status.updated_at))}</TicketValue>
                                            </TicketDetailRow>
                                        )}
                                        {!!el?.order?.fulfillment_status?.status && (
                                            <StatusChipWrapper>
                                                <EstablishmentOrderStatusChips status={el.order.fulfillment_status.status} />
                                            </StatusChipWrapper>
                                        )}
                                    </TicketTop>

                                    <TicketCutout />

                                    <TicketBottom dimmed={productStatus[index] === 'expired' || productStatus[index] === 'used'}>
                                        {productStatus[index] !== 'used' && productStatus[index] !== 'expired' ? (
                                            <QrButton onClick={(e) => { e.stopPropagation(); openModalQRCode(el.id); }}>
                                                Ver QR Code
                                            </QrButton>
                                        ) : (
                                            <QrCodeWrapper>
                                                <QRCode
                                                    value={el.code}
                                                    size={100}
                                                    bgColor="#ffffff"
                                                    fgColor="#000000"
                                                    level="Q"
                                                    renderAs="svg"
                                                    includeMargin={false}
                                                />
                                            </QrCodeWrapper>
                                        )}
                                        <CodeText>Código: <CodeValue>{el?.code}</CodeValue></CodeText>
                                    </TicketBottom>

                                    {visibleQrCode[el.id] && (
                                        <Modal isOpen={visibleQrCode[el.id]} onClose={() => closeModalQRCode(el.id)}>
                                            <ModalContent>
                                                <ModalTitle>Apresente este QR Code para retirar seu produto</ModalTitle>
                                                <QrCodeWrapper>
                                                    <QRCode
                                                        value={el.code}
                                                        size={180}
                                                        bgColor="#ffffff"
                                                        fgColor="#000000"
                                                        level="Q"
                                                        renderAs="svg"
                                                        includeMargin={false}
                                                    />
                                                </QrCodeWrapper>
                                                <CodeText style={{ marginTop: 12 }}>Código: <CodeValue>{el?.code}</CodeValue></CodeText>
                                                <ModalCloseBtn onClick={() => closeModalQRCode(el.id)}>Fechar</ModalCloseBtn>
                                            </ModalContent>
                                        </Modal>
                                    )}
                                </TicketCard>
                            ))}
                        </Carousel>

                        {products.length > 1 && (
                            <Dots>
                                {products.map((_, i) => (
                                    <Dot key={i} active={i === activeProductIdx} onClick={() => scrollTo(productScrollRef, i)} />
                                ))}
                            </Dots>
                        )}
                    </Section>
                )}
            </Content>
        </Container>
    );
}

/* ===== Layout ===== */

const Container = styled.div`
    width: 100%;
    min-height: 100%;
    margin-top: 100px;
    padding-bottom: 60px;
`;

const TopBar = styled.div`
    display: flex;
    align-items: center;
    padding: 0 16px 16px 16px;
    max-width: 600px;
    margin: 0 auto;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 0 0 20px 0;
    }
`;

const BackButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    cursor: pointer;
    transition: all .2s ease;
    flex-shrink: 0;

    &:hover {
        border-color: ${colors.nightLight};
        background: ${colors.neutralDark};
    }
`;

const TopBarTitle = styled.h2`
    flex: 1;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const TopBarSpacer = styled.div`
    width: 36px;
    flex-shrink: 0;
`;

const Content = styled.div`
    max-width: 600px;
    margin: 0 auto;
    padding: 0 16px;

    @media screen and (min-width: ${breakpoints.md}) {
        padding: 0;
    }
`;

/* ===== Event Card ===== */

const EventCard = styled.div`
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 14px;
    overflow: hidden;
    margin-bottom: 24px;
`;

const EventImageWrapper = styled.div`
    width: 100%;
    height: 180px;
    overflow: hidden;

    @media screen and (min-width: ${breakpoints.md}) {
        height: 220px;
    }

    & > div {
        width: 100%;
        height: 100%;
    }
`;

const EventImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const EventBody = styled.div`
    padding: 20px;
`;

const EventName = styled.h2`
    font-size: 18px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0 0 16px 0;
    line-height: 1.3;
`;

const EventInfoList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const EventInfoItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;

    & > svg {
        flex-shrink: 0;
        margin-top: 1px;
    }
`;

const EventInfoText = styled.span`
    font-size: 14px;
    color: ${colors.night};
    line-height: 1.4;
`;

/* ===== Section ===== */

const Section = styled.div`
    margin-bottom: 24px;
`;

const SectionHeader = styled.div`
    margin-bottom: 14px;
`;

const SectionTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 2px 0;
`;

const SectionHint = styled.p`
    font-size: 13px;
    color: ${colors.night};
    margin: 0;
`;

/* ===== Carousel ===== */

const CarouselNav = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin-bottom: 12px;
`;

const NavArrow = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    cursor: pointer;
    transition: all .2s ease;
    color: ${colors.nightDark};

    ${({ disabled }) => disabled && css`
        opacity: 0.3;
        pointer-events: none;
    `}

    &:hover {
        border-color: ${colors.nightLight};
    }
`;

const CarouselCounter = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
    min-width: 50px;
    text-align: center;
`;

const Carousel = styled.div`
    display: flex;
    gap: 16px;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: 4px;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const Dots = styled.div`
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-top: 14px;
`;

const Dot = styled.button`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: none;
    padding: 0;
    cursor: pointer;
    transition: all .2s ease;
    background: ${({ active }) => active ? colors.occasionalPurple : colors.neutralDark};

    ${({ active }) => active && css`
        width: 20px;
        border-radius: 4px;
    `}
`;

/* ===== Ticket Card ===== */

const TicketCard = styled.div`
    position: relative;
    min-width: 100%;
    scroll-snap-align: start;
    background: ${colors.neutralLight};
    border: 1px solid ${colors.neutralDark};
    border-radius: 14px;
    overflow: hidden;
`;

const StatusBanner = styled.div`
    width: 100%;
    padding: 8px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${colors.neutralLight};
    background: ${({ status }) => status === 'expired' ? colors.danger : colors.occasionalOrange};
`;

const TicketTop = styled.div`
    padding: 20px;

    ${({ dimmed }) => dimmed && css`
        opacity: 0.45;
    `}
`;

const TicketCutout = styled.div`
    position: relative;
    height: 24px;
    border-top: 2px dashed ${colors.neutralDark};
    margin: 0 20px;

    &::before,
    &::after {
        content: '';
        position: absolute;
        top: -13px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: ${colors.neutralBackground};
    }

    &::before {
        left: -32px;
    }

    &::after {
        right: -32px;
    }
`;

const TicketBottom = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0 20px 20px 20px;
    gap: 12px;

    ${({ dimmed }) => dimmed && css`
        opacity: 0.45;
    `}
`;

const TicketName = styled.h4`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    margin: 0 0 12px 0;
    line-height: 1.3;
`;

const TicketDetailRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 0;
    border-bottom: 1px solid ${colors.neutralDark};

    &:last-of-type {
        border-bottom: none;
    }
`;

const TicketLabel = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
`;

const TicketValue = styled.span`
    font-size: 13px;
    color: ${colors.nightDark};
    text-align: right;
`;

const QrButton = styled.button`
    flex: 1;
    padding: 12px;
    border-radius: 10px;
    border: none;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &:hover {
        opacity: 0.9;
        box-shadow: 0 4px 12px ${colors.occasionalPurple}40;
    }

    &:active {
        transform: scale(0.98);
    }
`;

const QrCodeWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 8px;
    background: ${colors.neutralLight};
    border-radius: 8px;
`;

const CodeText = styled.span`
    font-size: 12px;
    color: ${colors.night};
    text-align: center;
`;

const CodeValue = styled.span`
    font-weight: 600;
    color: ${colors.nightDark};
    letter-spacing: 0.05em;
`;

/* ===== Product specific ===== */

const ProductHeader = styled.div`
    display: flex;
    gap: 14px;
    margin-bottom: 12px;
`;

const ProductImage = styled.img`
    width: 72px;
    height: 72px;
    object-fit: cover;
    border-radius: 10px;
    flex-shrink: 0;
`;

const ProductInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const StatusChipWrapper = styled.div`
    margin-top: 8px;
`;

/* ===== Modal ===== */

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
`;

const ModalTitle = styled.p`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    text-align: center;
    margin: 0 0 8px 0;
`;

const ModalCloseBtn = styled.button`
    margin-top: 12px;
    padding: 10px 32px;
    border-radius: 8px;
    border: 1.5px solid ${colors.neutralDark};
    background: transparent;
    color: ${colors.night};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;

    &:hover {
        background: ${colors.neutralDark};
        border-color: ${colors.nightLight};
    }
`;

/* ===== Transfer ===== */

const TicketActionsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

const TicketActions = styled.div`
    display: flex;
    gap: 10px;
    width: 100%;
`;

const TransferButton = styled.button`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px;
    border-radius: 10px;
    border: 1.5px solid ${colors.occasionalPurple};
    background: transparent;
    color: ${colors.occasionalPurple};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &:hover {
        background: ${colors.occasionalPurple}10;
    }

    &:active {
        transform: scale(0.98);
    }
`;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const SpinnerIcon = styled.span`
    display: inline-flex;
    animation: ${spin} 1s linear infinite;
`;

const DownloadButton = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px;
    border-radius: 10px;
    border: 1.5px solid ${colors.occasionalPurple};
    background: transparent;
    color: ${colors.occasionalPurple};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &:hover {
        background: ${colors.occasionalPurple}10;
    }

    &:active {
        transform: scale(0.98);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const TransferModalContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 100%;
`;

const TransferModalTitle = styled.h3`
    font-size: 18px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 4px 0;
    text-align: center;
`;

const TransferModalSubtitle = styled.p`
    font-size: 14px;
    color: ${colors.night};
    margin: 0 0 16px 0;
    text-align: center;
`;

const TransferFieldGroup = styled.div`
    width: 100%;
    margin-bottom: 12px;
`;

const TransferInputLabel = styled.label`
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.night};
    margin-bottom: 6px;
`;

const TransferInput = styled.input`
    width: 100%;
    padding: 12px 14px;
    border-radius: 10px;
    border: 1.5px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    font-size: 14px;
    color: ${colors.nightDark};
    outline: none;
    transition: border-color .2s ease;
    box-sizing: border-box;

    &::placeholder {
        color: ${colors.nightLight};
    }

    &:focus {
        border-color: ${colors.occasionalPurple};
    }
`;

const ErrorText = styled.p`
    font-size: 13px;
    color: ${colors.danger};
    margin: 4px 0 8px 0;
    text-align: center;
    width: 100%;
`;

const CertificateSection = styled.div`
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid ${colors.neutralDark};
`;

const CertificateSectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
`;

const CertificateSectionTitle = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CertificateDownloadButton = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 12px;
    border-radius: 10px;
    border: 1.5px solid ${colors.occasionalPurple};
    background: transparent;
    color: ${colors.occasionalPurple};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &:hover {
        background: ${colors.occasionalPurple}10;
    }

    &:active {
        transform: scale(0.98);
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
    }
`;

const TransferSubmitButton = styled.button`
    width: 100%;
    padding: 14px;
    border-radius: 10px;
    border: none;
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    margin-top: 8px;

    &:hover {
        opacity: 0.9;
        box-shadow: 0 4px 12px ${colors.occasionalPurple}40;
    }

    &:active {
        transform: scale(0.98);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;
